import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { latLangRange, latLangDMSRange, latLangDMRange } from 'src/app/shared/directives/latlong-validator.directive';
import { MapDataService } from 'src/app/dashboard/components/services/map-data.service';
import { pairwise, startWith } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { SelectedCoordinate } from '../itinerary';

@Component({
  selector: 'app-itinenary-crud',
  templateUrl: './itinenary-crud.dialog.html',
  styleUrls: ['./itinenary-crud.dialog.scss']
})
export class ItinenaryCrudDialog implements OnInit {

  mode = 'Add';
  crudForm: FormGroup;
  currentLatLongSubs: Subscription[] = [];
  selectedCoordinateSub: Subscription;

  constructor(
    private dataService: MapDataService,
    public dialogRef: MatDialogRef<ItinenaryCrudDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any, ) {
    this.mode = data.mode;
  }

  ngOnInit() {
    this.crudForm = new FormGroup({
      selectedCoordinate: new FormControl(SelectedCoordinate.DD),
      comment: new FormControl(''),
      elevation: new FormControl(''),
      itineraryName: new FormControl(''),
      latitude: new FormControl(''),
      longitude: new FormControl(''),
      latitudeDD: new FormControl(0, [Validators.required, latLangRange(-90, 90)]),
      latitudeDM: new FormControl({ degree: 0, minutes: 0, seconds: NaN }, [Validators.required, latLangDMRange(-90, 90)]),
      latitudeDMS: new FormControl({ degree: 0, minutes: 0, seconds: 0 }, [Validators.required, latLangDMSRange(-90, 90)]),
      longitudeDD: new FormControl(0, [Validators.required, latLangRange(-180, 180)]),
      longitudeDM: new FormControl({ degree: 0, minutes: 0, seconds: NaN }, [Validators.required, latLangDMRange(-180, 180)]),
      longitudeDMS: new FormControl({ degree: 0, minutes: 0, seconds: 0 }, [Validators.required, latLangDMSRange(-180, 180)]),
      latDirection: new FormControl('N'),
      longDirection: new FormControl('E')
    });
    this.initializeFormValues();
    this.onCoordinateChange();
  }

  initializeFormValues(): void {
    if (this.mode === 'Edit') {
      this.crudForm.patchValue({
        selectedCoordinate: this.data.selectedCoordinate,
        comment: this.data.comment,
        elevation: this.data.elevation,
        itineraryName: this.data.itineraryName,
        latitudeDD: this.data.latitudeDD,
        longitudeDD: this.data.longitudeDD,
        latitudeDM: this.strToObjConversion(this.data.latitudeDM, 'DM'),
        longitudeDM: this.strToObjConversion(this.data.longitudeDM, 'DM'),
        latitudeDMS: this.strToObjConversion(this.data.latitudeDMS, 'DMS'),
        longitudeDMS: this.strToObjConversion(this.data.longitudeDMS, 'DMS')
      });
    }
  }

  onCoordinateChange() {
    this.selectedCoordinateSub = this.crudForm.get('selectedCoordinate')
      .valueChanges
      .pipe(startWith(0), pairwise())
      .subscribe(
        ([prevItem, nextItem]: [number, number]) => {
          const selectedItem = prevItem | 0;
          const payload = this.coordinateBasedValueChange(selectedItem);
          this.getComputedCoordinates(payload).then(
            res => {
              this.initializeComputedLatLong(res);
            },
            err => {
              console.log(err);
            }
          )
        }
      );
  }

  coordinateBasedValueChange(type: number) {
    let payload = null;
    switch (type) {
      case SelectedCoordinate.DD:
        payload = this.getFormDataForComputeCoordinate('DD', SelectedCoordinate.DD);
        break;
      case SelectedCoordinate.DM:
        payload = this.getFormDataForComputeCoordinate('DM', SelectedCoordinate.DM);
        break;
      case SelectedCoordinate.DMS:
        payload = this.getFormDataForComputeCoordinate('DMS', SelectedCoordinate.DMS);
        break;
      default: break;
    }
    return payload;
  }

  getFormDataForComputeCoordinate(type: string, coordinateType: SelectedCoordinate) {
    const latLabel = `latitude${type}`;
    const longLabel = `longitude${type}`;

    const latFormVal = this.crudForm.controls[latLabel].value;
    const longFormVal = this.crudForm.controls[longLabel].value;

    const latitudeVal = type === 'DD' ? latFormVal : this.objToStrConversion(latFormVal, type);
    const longitudeVal = type === 'DD' ? longFormVal : this.objToStrConversion(longFormVal, type);

    const payload = {
      [latLabel]: String(latitudeVal),
      [longLabel]: String(longitudeVal),
      selectedCoordinate: coordinateType
    };
    return payload;
  }

  async getComputedCoordinates(payload) {
    return new Promise<any>((resolve, reject) => {
      this.dataService.getComputedCoordinates(payload).subscribe((res: any) => {
        if (res.responseStatus === 'Success') {
          resolve(res.responseData)
        } else {
          reject(res.responseStatus);
        }
      }, err => {
        reject(err);
        console.log('Error while fetching computed coordinates', err);
      });
    });
  }
  initializeComputedLatLong(payload) {
    const { latitudeDMS, longitudeDMS, latitudeDM, longitudeDM, latitudeDD, longitudeDD } = payload;
    this.crudForm.patchValue({
      longitudeDMS: this.strToObjConversion(longitudeDMS, 'DMS'),
      latitudeDMS: this.strToObjConversion(latitudeDMS, 'DMS'),
      longitudeDM: this.strToObjConversion(longitudeDM, 'DM'),
      latitudeDM: this.strToObjConversion(latitudeDM, 'DM'),
      longitudeDD,
      latitudeDD,
    });
  }
  strToObjConversion(latLongStr: Number | null, type: string) {
    if (latLongStr) {
      const str = String(latLongStr)
      const degree = Number(str.split('°')[0]);
      const minutes = Number(str.split('°')[1].split("'")[0]);
      const seconds = type === 'DMS' ? Number(str.split('°')[1].split("'")[1].split('"')[0]) : NaN
      return { degree, minutes, seconds };
    }
    return {
      degree: 0,
      minutes: 0,
      seconds: type === 'DMS' ? 0 : NaN
    };
  }
  objToStrConversion(obj: any | null, type: string): string {
    let DMS = `0°0'0"`;
    let DM = `0°0'`;
    if (obj) {
      DMS = `${obj.degree}°${obj.minutes}'${obj.seconds}"`;
      DM = `${obj.degree}°${obj.minutes}'`;
      return type === 'DMS' ? DMS : DM;
    }
    return type === 'DMS' ? DMS : DM;
  }
  cancel(): void {
    this.dialogRef.close({ saveData: false });
  }
  prepareFinalPayload() {
    // Include non form data - (id,sequenceId)
    const { id, sequenceId } = this.data;
    //Decimal Conversion
    let { latitudeDM, latitudeDMS, longitudeDM, longitudeDMS } = this.crudForm.value
    let formVal = { ...this.crudForm.value }
    formVal.latitude = formVal.latitudeDD = parseFloat(formVal.latitudeDD).toPrecision(8);
    formVal.longitude = formVal.longitudeDD = parseFloat(formVal.longitudeDD).toPrecision(8)
    formVal.latitudeDM = this.objToStrConversion(latitudeDM, 'DM');
    formVal.longitudeDM = this.objToStrConversion(longitudeDM, 'DM');
    formVal.latitudeDMS = this.objToStrConversion(latitudeDMS, 'DMS');
    formVal.longitudeDMS = this.objToStrConversion(longitudeDMS, 'DMS');
    delete formVal.latDirection
    delete formVal.longDirection

    const finalPayload = {
      wayPointVal: {
        ...formVal,
        id,
        sequenceId,
      },
      saveData: true
    }
    return finalPayload;
  }
  onSubmit(): void {
    // console.log(this.crudForm);
    if (this.crudForm.valid) {
      const payload = this.coordinateBasedValueChange(this.crudForm.get('selectedCoordinate').value);
      this.getComputedCoordinates(payload).then(
        res => {
          this.initializeComputedLatLong(res);
          const finalPayload = this.prepareFinalPayload();
          this.dialogRef.close(finalPayload);
        }
      ).catch(err => {
        const finalPayload = this.prepareFinalPayload();
        this.dialogRef.close(finalPayload);
        console.log(err);
      });

    }
  }

}
